import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  View, Text, StyleSheet, Animated, PanResponder,
} from 'react-native';
import { IconButton } from 'react-native-paper';
import { Audio } from 'expo-av';
import { theme } from '../../screens/Common.style';

const MAX_DURATION = 30;

export default function GameVoiceMessageRecorder({ onSend, disabled }) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [cancelled, setCancelled] = useState(false);

  const recordingRef = useRef(null);
  const timerRef = useRef(null);
  const scaleAnim = useRef(new Animated.Value(1)).current;

  // Refs so PanResponder callbacks (created once) always read current values
  const cancelledRef = useRef(false);
  const elapsedRef = useRef(0);
  const recordingActiveRef = useRef(false);

  const startRecording = useCallback(async () => {
    if (recordingActiveRef.current) return;
    try {
      await Audio.requestPermissionsAsync();
      await Audio.setAudioModeAsync({ allowsRecordingIOS: true, playsInSilentModeIOS: true });

      const { recording } = await Audio.Recording.createAsync(
        Audio.RecordingOptionsPresets.HIGH_QUALITY,
      );
      recordingRef.current = recording;
      recordingActiveRef.current = true;
      cancelledRef.current = false;
      elapsedRef.current = 0;
      setCancelled(false);
      setElapsed(0);
      setRecording(true);

      Animated.spring(scaleAnim, { toValue: 1.3, useNativeDriver: true }).start();

      timerRef.current = setInterval(() => {
        elapsedRef.current += 1;
        setElapsed(elapsedRef.current);
        if (elapsedRef.current >= MAX_DURATION) {
          stopRecordingRef.current(false);
        }
      }, 1000);
    } catch {}
  }, []);

  const stopRecording = useCallback(async (cancel = false) => {
    if (!recordingActiveRef.current) return;
    recordingActiveRef.current = false;

    clearInterval(timerRef.current);
    Animated.spring(scaleAnim, { toValue: 1, useNativeDriver: true }).start();
    setRecording(false);

    if (!recordingRef.current) return;

    const shouldCancel = cancel || cancelledRef.current;

    try {
      await recordingRef.current.stopAndUnloadAsync();
    } catch {}

    if (shouldCancel) {
      recordingRef.current = null;
      setCancelled(false);
      cancelledRef.current = false;
      return;
    }

    const uri = recordingRef.current.getURI();
    const duration = elapsedRef.current;
    recordingRef.current = null;

    if (uri && duration > 0) {
      onSend({ uri, duration });
    }
  }, [onSend]);

  // Always point to the latest stopRecording so the PanResponder ref can call it
  const stopRecordingRef = useRef(stopRecording);
  const startRecordingRef = useRef(startRecording);
  useEffect(() => { stopRecordingRef.current = stopRecording; }, [stopRecording]);
  useEffect(() => { startRecordingRef.current = startRecording; }, [startRecording]);

  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onPanResponderGrant: () => {
        cancelledRef.current = false;
        setCancelled(false);
        startRecordingRef.current();
      },
      onPanResponderMove: (_, gestureState) => {
        const isCancelling = gestureState.dx < -60;
        cancelledRef.current = isCancelling;
        setCancelled(isCancelling);
      },
      onPanResponderRelease: () => {
        stopRecordingRef.current(cancelledRef.current);
      },
      onPanResponderTerminate: () => {
        stopRecordingRef.current(true);
      },
    }),
  ).current;

  const elapsedLabel = `${Math.floor(elapsed / 60)}:${(elapsed % 60).toString().padStart(2, '0')}`;

  return (
    <View style={styles.container}>
      {recording && (
        <View style={styles.recordingIndicator}>
          {cancelled ? (
            <Text style={styles.cancelLabel}>⟵ Glissez pour annuler</Text>
          ) : (
            <Text style={styles.timer}>{elapsedLabel}</Text>
          )}
          <View style={[styles.dot, cancelled && styles.dotCancelled]} />
        </View>
      )}
      <Animated.View
        {...panResponder.panHandlers}
        style={[
          styles.micButton,
          disabled && styles.micDisabled,
          { transform: [{ scale: scaleAnim }] },
          recording && styles.micRecording,
          cancelled && styles.micCancelled,
        ]}
      >
        <IconButton
          icon={recording ? 'microphone' : 'microphone-outline'}
          size={24}
          iconColor={recording ? '#fff' : (disabled ? '#bbb' : theme.colors.primary)}
          pointerEvents="none"
        />
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flexDirection: 'row', alignItems: 'center' },
  recordingIndicator: { flexDirection: 'row', alignItems: 'center', gap: 8, marginRight: 8 },
  timer: { fontSize: 14, fontWeight: '700', color: theme.colors.primary },
  cancelLabel: { fontSize: 12, color: '#e53935' },
  dot: { width: 8, height: 8, borderRadius: 4, backgroundColor: '#e53935' },
  dotCancelled: { backgroundColor: '#bbb' },
  micButton: { borderRadius: 24, backgroundColor: '#f0f0f0' },
  micRecording: { backgroundColor: theme.colors.primary },
  micCancelled: { backgroundColor: '#e53935' },
  micDisabled: { opacity: 0.4 },
});
